import { Copy, Check, Sparkles, RefreshCw, FileText, Info } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

interface CompleteCipherPanelProps {
  cipherName: string;
  cipherCode?: string;
  onCipherCodeChange?: (code: string) => void;
}

// The new API mints the cipher code server-side at create-time, so the
// preview here is purely a client-side suggestion (8 chars, A-Z + 0-9 minus
// confusables). Users can regenerate; the eventual code returned by
// `client.createCipher` is what gets persisted.
const PREVIEW_ALPHABET = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
function generatePreviewCode(): string {
  let out = "";
  const buf = new Uint32Array(8);
  crypto.getRandomValues(buf);
  for (let i = 0; i < 8; i++) {
    const idx = (buf[i] ?? 0) % PREVIEW_ALPHABET.length;
    out += PREVIEW_ALPHABET[idx];
  }
  return out;
}

export default function CompleteCipherPanel({
  cipherName,
  cipherCode,
  onCipherCodeChange,
}: CompleteCipherPanelProps) {
  const [copied, setCopied] = useState(false);
  const [localCode, setLocalCode] = useState(cipherCode || "");

  const handleCopyCode = () => {
    const codeToCopy = localCode || cipherCode;
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleRandomize = () => {
    const next = generatePreviewCode();
    setLocalCode(next);
    onCipherCodeChange?.(next);
  };

  const displayCode = localCode || cipherCode;

  return (
    <div className="space-y-3">
      {/* Cipher Name */}
      <div className="flex items-center gap-3 p-3 rounded-lg border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <div className="flex items-center justify-center h-10 w-10 rounded-lg bg-purple-100 shrink-0">
          <FileText className="h-5 w-5 text-purple-600" />
        </div>
        <div className="min-w-0">
          <p className="text-xs text-gray-500">Cipher Name</p>
          <p className="text-sm font-bold text-gray-900 truncate">{cipherName}</p>
        </div>
      </div>

      {/* Cipher Code */}
      <div className="p-3 rounded-lg border-2 border-purple-300 bg-gradient-to-br from-purple-100 to-blue-100">
        <div className="flex items-center gap-2 mb-2">
          <Sparkles className="h-4 w-4 text-purple-600" />
          <h4 className="text-sm font-semibold text-gray-800">Your Cipher ID</h4>
        </div>

        {displayCode ? (
          <div className="space-y-2">
            <div className="flex items-center gap-2 bg-white/50 px-3 py-2 rounded-lg border border-purple-200">
              <code className="flex-1 text-xl font-bold text-gray-900 font-mono tracking-wider text-center">
                {displayCode}
              </code>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleCopyCode}
                className="h-8 w-8 p-0 shrink-0"
                title="Copy to clipboard"
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-600" />
                ) : (
                  <Copy className="h-4 w-4" />
                )}
              </Button>
            </div>
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-600">Share this ID to let others use your cipher</p>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRandomize}
                className="h-7 text-xs gap-1 px-2"
              >
                <RefreshCw className="h-3 w-3" />
                New ID
              </Button>
            </div>
          </div>
        ) : (
          <Button
            onClick={handleRandomize}
            className="w-full flex items-center justify-center gap-2 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white"
          >
            <Sparkles className="h-4 w-4" />
            Generate Cipher ID
          </Button>
        )}
      </div>

      {/* Info Note */}
      <div className="flex items-center gap-3 p-3 rounded-lg border border-green-200 bg-green-50">
        <Info className="h-4 w-4 text-green-600 shrink-0" />
        <p className="text-xs text-green-800">
          {displayCode ? "Cipher ID set." : "Generate your Cipher ID, then"} Click "Deploy Cipher" to go live.
        </p>
      </div>
    </div>
  );
}

